// Pure display helpers for the MCP SERVER's screens — the staff "MCP Tools"
// tab (dashboard/McpServerToolsManager.jsx), the user Settings "MCP" section
// (pages/McpServerSettings.jsx) and the OAuth consent page
// (pages/OAuthConsentPage.jsx). Anything two of them must agree on — how tools
// group by family, and how an app's self-chosen name is made safe to show —
// lives here once rather than twice. See
// docs/superpowers/specs/2026-09-21-mcp-tool-governance-design.md (§4, §7).
//
// Naming note: this is about the tools THIS app's own MCP SERVER exposes to
// external clients (Claude, ChatGPT). It is unrelated to
// `client/src/mcp-connectors/` + `services/mcp/`, which is this app acting as
// an MCP CLIENT to third-party servers — deliberately not named anything
// with "Connectors" in it so the two features stay easy to tell apart.
//
// `family` on every tool comes from the Worker's catalog (`GET
// /internal/tools`, spec §4: context | files | workflows | library |
// data_sources). This module NEVER re-derives it from a tool's name — an
// earlier route in this feature was flagged in review for re-deriving
// "store beats global" instead of reading the server's own decision, and the
// same principle applies here: family is read, not recomputed.

/** Known catalog families, in the order they should read when every family
 * is present. A family outside this list — e.g. one added to the catalog
 * later — still groups correctly; it sorts after the known ones, in the
 * order it is first seen. */
const KNOWN_FAMILY_ORDER = ['account', 'memory', 'context', 'files', 'workflows', 'library', 'data_sources'];

const FAMILY_LABELS = {
    context: 'Context',
    files: 'Files',
    workflows: 'Workflows',
    library: 'Library',
    data_sources: 'Data sources',
    memory: 'Memory',
    account: 'Account & workspace',
};

/** A tool with no family (e.g. the catalog was unreachable and this entry
 * was synthesized from a raw stored key) groups under this bucket rather
 * than being dropped — every tool lands in exactly one family. */
const UNGROUPED_FAMILY = 'other';

/** Display label for a family key. Unknown families fall back to a
 * title-cased rendering of the key itself. */
export function familyLabel(family) {
    if (FAMILY_LABELS[family]) return FAMILY_LABELS[family];
    if (!family || family === UNGROUPED_FAMILY) return 'Other';
    return String(family)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Groups tools by their own `family` field. Every tool lands in exactly one
 * group — a missing/falsy family goes to `other`. Group order is stable:
 * known families first (KNOWN_FAMILY_ORDER), then any other family in the
 * order it is first seen in `tools`, then `other` last. Each group keeps its
 * tools' original relative order from `tools`.
 *
 * @param {Array<{ name: string, family?: string|null }>} tools
 * @returns {Array<{ family: string, label: string, tools: Array }>}
 */
export function groupToolsByFamily(tools) {
    if (!Array.isArray(tools)) return [];

    const byFamily = new Map();
    const seenOrder = [];
    for (const tool of tools) {
        const family = tool?.family || UNGROUPED_FAMILY;
        if (!byFamily.has(family)) {
            byFamily.set(family, []);
            seenOrder.push(family);
        }
        byFamily.get(family).push(tool);
    }

    const orderedFamilies = [
        ...KNOWN_FAMILY_ORDER.filter((f) => byFamily.has(f)),
        ...seenOrder.filter((f) => f !== UNGROUPED_FAMILY && !KNOWN_FAMILY_ORDER.includes(f)),
        ...(byFamily.has(UNGROUPED_FAMILY) ? [UNGROUPED_FAMILY] : []),
    ];

    return orderedFamilies.map((family) => ({
        family,
        label: familyLabel(family),
        tools: byFamily.get(family),
    }));
}

/**
 * The three states the staff per-store control shows for one tool, from the
 * SAME resolved entry `GET /admin/mcp-tools/stores/:shop` already returns
 * (`{ enabled, source, globalEnabled }`, computed server-side by
 * mcpToolPolicy.js#staffToolViewFor — one policy read, one evaluator). This
 * function only turns that decision into display state; it never re-derives
 * "a store override beats global" itself — `source` already carries that.
 *
 * `source === 'store'` means an explicit override exists here: the state is
 * that override's own value. Any other source ('global' or 'default') means
 * there is no override — the state is 'inherit'.
 *
 * `inheritedEnabled` is what choosing "Inherit" would make the tool, and it is
 * READ from the server's `globalEnabled` — never worked out here. It matters
 * most on a store override: an override set Off while global is On inherits
 * ON, and a label that guessed "Off" there once led a staffer to pick "On"
 * instead — a sticky override that defeats a later global Off, the incident
 * kill switch. With no override, what the tool is now IS what it inherits
 * (the server sends `globalEnabled === enabled` then), so `enabled` stands in
 * when an entry carries no `globalEnabled`. On an override with no
 * `globalEnabled` (the catalog-unavailable fallback rows) it is `null`:
 * unknown, and labelled as such rather than guessed.
 *
 * @param {{ enabled: boolean, source: 'store' | 'global' | 'default', globalEnabled?: boolean }} entry
 * @returns {{ state: 'inherit' | 'on' | 'off', inheritedEnabled: boolean | null }}
 */
export function resolveThreeState(entry) {
    const enabled = entry?.enabled !== false;
    const globalEnabled = typeof entry?.globalEnabled === 'boolean' ? entry.globalEnabled : null;
    if (entry?.source === 'store') {
        return { state: enabled ? 'on' : 'off', inheritedEnabled: globalEnabled };
    }
    return { state: 'inherit', inheritedEnabled: globalEnabled ?? enabled };
}

/** The "Inherit" option's label for resolveThreeState's `inheritedEnabled`:
 * "Inherit (On)", "Inherit (Off)", or plain "Inherit" when it is unknown. */
export function inheritOptionLabel(inheritedEnabled) {
    if (inheritedEnabled === true) return 'Inherit (On)';
    if (inheritedEnabled === false) return 'Inherit (Off)';
    return 'Inherit';
}

/** The PUT payload value for a three-state pick: 'on' -> true, 'off' ->
 * false, 'inherit' -> null (clears the store override, per spec §5.3's
 * `PUT /admin/mcp-tools/stores/:shop` — `null` means "back to inherit"). */
export function threeStateToOverrideValue(state) {
    if (state === 'on') return true;
    if (state === 'off') return false;
    return null;
}

// An MCP app's name is chosen by the app itself, through the Worker's OAuth
// Dynamic Client Registration — self-service, attacker-controlled text, passed
// through unsanitised to the consent page (`?client=`) and to Settings'
// connected-app list (`clientName`). Rendering it is safe from injection
// (React escapes; no dangerouslySetInnerHTML), but:
//   - line breaks can rewrite a confirmation around it — a reviewer's probe
//     turned Settings' Disconnect prompt into "…This will disconnect ALL
//     apps. Press Cancel to keep them.", an app talking the user out of
//     revoking it;
//   - bidi override/isolate characters can make it read as something it
//     isn't (e.g. reversed to spell a trusted name);
//   - an unbounded string can push Allow/Deny off screen.
// So: strip C0/C1 controls (which include \n and \r), the Unicode line and
// paragraph separators (which can render as line breaks too) and the bidi
// override/isolate range, then cap the length. This is the ONE implementation;
// every screen that shows an app's name imports it.
//
// It is NOT what keeps a name from forging our own words. Stripping is a
// blocklist, and a blocklist loses: it kept `"`, and a name that closed its own
// quotation passed straight through into Settings' native Disconnect prompt
// (look-alike quote characters would have been next). Structure is what
// protects: a name is only ever shown in its own labelled field, never inside
// a sentence we wrote — see McpServerSettings.jsx's in-page confirmation.
const MAX_CLIENT_NAME_LENGTH = 60;
// Character codes (not literal characters) so this file never itself embeds
// the bidi override/isolate control characters it exists to strip.
const UNSAFE_NAME_CODEPOINTS = [
    [0x0000, 0x001f], // C0 controls
    [0x007f, 0x009f], // C1 controls
    [0x2028, 0x2029], // LINE SEPARATOR, PARAGRAPH SEPARATOR
    [0x202a, 0x202e], // LRE, RLE, PDF, LRO, RLO
    [0x2066, 0x2069], // LRI, RLI, FSI, PDI
];
const UNSAFE_NAME_CHARS_RE = new RegExp(
    `[${UNSAFE_NAME_CODEPOINTS.map(([from, to]) => `\\u{${from.toString(16)}}-\\u{${to.toString(16)}}`).join('')}]`,
    'gu',
);

/**
 * `rawName` made safe to put on screen or in an aria-label: controls, line and
 * paragraph separators and bidi overrides removed, trimmed, and capped at
 * 60 characters (with "…"). A name that is empty afterwards becomes
 * `fallback` — the consent page's "An application" unless a caller names its
 * own.
 *
 * @param {unknown} rawName
 * @param {string} [fallback]
 * @returns {string}
 */
/**
 * What a connected app can reach, when it covers more than one account
 * (a coverage grant, 2026-09-24): "All accounts & workspaces (including new
 * ones)", or "N accounts: A, B, …". Null for a single-store app, which shows
 * its workspace instead. Account names are typed by the accounts' own users,
 * so each goes through the same sanitizer as an app name.
 */
export function accessSummary(app) {
  const coverage = app?.coverage;
  if (!coverage) return null;
  if (coverage.all === true) return 'All accounts & workspaces (including new ones)';
  const accounts = Array.isArray(coverage.accounts) ? coverage.accounts : [];
  const names = accounts.map((a) => sanitizeClientName(a?.name, 'Unnamed account'));
  const shown = names.slice(0, 3).join(', ');
  const more = names.length > 3 ? ` and ${names.length - 3} more` : '';
  return `${names.length} account${names.length === 1 ? '' : 's'}: ${shown}${more}`;
}

export function sanitizeClientName(rawName, fallback = 'An application') {
    const cleaned = String(rawName || '').replace(UNSAFE_NAME_CHARS_RE, '').trim();
    if (!cleaned) return fallback;
    if (cleaned.length <= MAX_CLIENT_NAME_LENGTH) return cleaned;
    return `${cleaned.slice(0, MAX_CLIENT_NAME_LENGTH)}…`;
}
